<script>
import { useAlert, useTrack } from 'dashboard/composables';
import BotMetrics from './components/BotMetrics.vue';
import ReportFilters from './components/ReportFilters.vue';
import { GROUP_BY_FILTER } from './constants';
import ReportContainer from './ReportContainer.vue';
import ConversationListReport from './components/ConversationListReport.vue';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportHeader from './components/ReportHeader.vue';

export default {
  name: 'BotReports',
  components: {
    BotMetrics,
    ReportHeader,
    ReportFilters,
    ReportContainer,
    ConversationListReport,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      reportKeys: {
        BOT_RESOLUTION_COUNT: 'bot_resolutions_count',
        BOT_HANDOFF_COUNT: 'bot_handoffs_count',
      },
      businessHours: false,
      activeView: 'chart',
    };
  },
  computed: {
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
      };
    },
  },
  methods: {
    fetchAllData() {
      this.fetchBotSummary();
      this.fetchChartData();
    },
    fetchBotSummary() {
      try {
        this.$store.dispatch('fetchBotSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      Object.keys(this.reportKeys).forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: this.reportKeys[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
      };
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours },
        reportType: 'bots',
      });
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('BOT_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4">
    <ReportFilters
      :show-entity-filter="false"
      show-group-by
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <div class="inline-flex items-center gap-1 p-1 rounded-lg bg-n-slate-3 w-fit">
      <button
        class="px-3 py-1 text-sm rounded-md"
        :class="activeView === 'chart' ? 'bg-n-solid-1 text-n-slate-12 shadow' : 'text-n-slate-11'"
        @click="activeView = 'chart'"
      >
        {{ $t('BOT_REPORTS.VIEW_CHART') }}
      </button>
      <button
        class="px-3 py-1 text-sm rounded-md"
        :class="activeView === 'list' ? 'bg-n-solid-1 text-n-slate-12 shadow' : 'text-n-slate-11'"
        @click="activeView = 'list'"
      >
        {{ $t('BOT_REPORTS.VIEW_LIST') }}
      </button>
    </div>

    <template v-if="activeView === 'chart'">
      <BotMetrics :filters="requestPayload" />
      <ReportContainer
        account-summary-key="getBotSummary"
        summary-fetching-key="getBotSummaryFetchingStatus"
        :group-by="groupBy"
        :report-keys="reportKeys"
      />
    </template>
    <ConversationListReport
      v-else
      :from="from"
      :to="to"
      force-ai-involved
      title="Captain AI Report"
      storage-key="captain_list_report_columns"
      :default-columns="[
        'conversation_id',
        'contact_name',
        'status',
        'resolved_by',
        'solved_by_ai',
        'handed_off_by_ai',
        'agent_takeover',
        'created_at',
      ]"
    />
  </div>
</template>
